import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	Target,
	Play,
	Loader2,
	Star,
	TrendingUp,
	Clock,
	CalendarCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/cn";
import { testKPI } from "@/services/simulation";
import { useToast } from "@/components/ui/use-toast";

const performanceLevelColors: Record<string, string> = {
	exceptional: "bg-green-500",
	high: "bg-blue-500",
	average: "bg-amber-500",
	below: "bg-orange-500",
	poor: "bg-red-500",
};

const performanceLevelStars: Record<string, number> = {
	exceptional: 5,
	high: 4,
	average: 3,
	below: 2,
	poor: 1,
};

export function KPICalculator() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		averageProductivityScore: 75,
		averageActiveTimePercent: 80,
		productiveAppTimePercent: 70,
		attendancePercentage: 95,
		punctualityPercentage: 90,
		totalWorkedHours: 160,
		expectedHours: 160,
		isFieldWorker: false,
	});

	const kpiMutation = useMutation({
		mutationFn: testKPI,
		onError: (error: any) => {
			toast({
				title: "Error",
				description: error.message || "Failed to calculate KPI",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		kpiMutation.mutate(formData);
	};

	const result = kpiMutation.data;

	const getScoreColor = (score: number) => {
		if (score >= 85) return "text-green-600";
		if (score >= 70) return "text-blue-600";
		if (score >= 50) return "text-amber-600";
		if (score >= 30) return "text-orange-600";
		return "text-red-600";
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Target className="w-6 h-6" />
						KPI Calculator
					</h1>
					<p className="text-gray-500 mt-1">
						Test KPI calculation with custom metrics
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>Input Metrics</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Field Worker Toggle */}
						<div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
							<div>
								<Label>Field Worker</Label>
								<p className="text-sm text-gray-500">
									No productivity monitoring
								</p>
							</div>
							<Switch
								checked={formData.isFieldWorker}
								onCheckedChange={(checked) =>
									setFormData({ ...formData, isFieldWorker: checked })
								}
							/>
						</div>

						{/* Productivity Metrics */}
						{!formData.isFieldWorker && (
							<div className="space-y-4">
								<h4 className="font-medium text-gray-700 flex items-center gap-2">
									<TrendingUp className="w-4 h-4" />
									Productivity Metrics
								</h4>
								<div>
									<div className="flex items-center justify-between mb-2">
										<Label>Avg Productivity Score</Label>
										<Badge variant="outline">{formData.averageProductivityScore}</Badge>
									</div>
									<Slider
										value={[formData.averageProductivityScore]}
										onValueChange={(values: number[]) =>
											setFormData({ ...formData, averageProductivityScore: values[0] })
										}
										max={100}
										step={1}
									/>
								</div>
								<div>
									<div className="flex items-center justify-between mb-2">
										<Label>Active Time %</Label>
										<Badge variant="outline">{formData.averageActiveTimePercent}%</Badge>
									</div>
									<Slider
										value={[formData.averageActiveTimePercent]}
										onValueChange={(values: number[]) =>
											setFormData({ ...formData, averageActiveTimePercent: values[0] })
										}
										max={100}
										step={1}
									/>
								</div>
								<div>
									<div className="flex items-center justify-between mb-2">
										<Label>Productive App Time %</Label>
										<Badge variant="outline">{formData.productiveAppTimePercent}%</Badge>
									</div>
									<Slider
										value={[formData.productiveAppTimePercent]}
										onValueChange={(values: number[]) =>
											setFormData({ ...formData, productiveAppTimePercent: values[0] })
										}
										max={100}
										step={1}
									/>
								</div>
							</div>
						)}

						{/* Attendance Metrics */}
						<div className="space-y-4">
							<h4 className="font-medium text-gray-700 flex items-center gap-2">
								<CalendarCheck className="w-4 h-4" />
								Attendance Metrics
							</h4>
							<div>
								<div className="flex items-center justify-between mb-2">
									<Label>Attendance %</Label>
									<Badge variant="outline">{formData.attendancePercentage}%</Badge>
								</div>
								<Slider
									value={[formData.attendancePercentage]}
									onValueChange={(values: number[]) =>
										setFormData({ ...formData, attendancePercentage: values[0] })
									}
									max={100}
									step={1}
								/>
							</div>
							<div>
								<div className="flex items-center justify-between mb-2">
									<Label>Punctuality %</Label>
									<Badge variant="outline">{formData.punctualityPercentage}%</Badge>
								</div>
								<Slider
									value={[formData.punctualityPercentage]}
									onValueChange={(values: number[]) =>
										setFormData({ ...formData, punctualityPercentage: values[0] })
									}
									max={100}
									step={1}
								/>
							</div>
						</div>

						{/* Output Metrics */}
						<div className="space-y-4">
							<h4 className="font-medium text-gray-700 flex items-center gap-2">
								<Clock className="w-4 h-4" />
								Output Metrics
							</h4>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label>Worked Hours</Label>
									<div className="flex items-center gap-2 mt-2">
										<Slider
											value={[formData.totalWorkedHours]}
											onValueChange={(values: number[]) =>
												setFormData({ ...formData, totalWorkedHours: values[0] })
											}
											max={200}
											step={1}
										/>
										<Badge variant="outline" className="min-w-[60px] justify-center">
											{formData.totalWorkedHours}h
										</Badge>
									</div>
								</div>
								<div>
									<Label>Expected Hours</Label>
									<div className="flex items-center gap-2 mt-2">
										<Slider
											value={[formData.expectedHours]}
											onValueChange={(values: number[]) =>
												setFormData({ ...formData, expectedHours: values[0] })
											}
											max={200}
											step={1}
										/>
										<Badge variant="outline" className="min-w-[60px] justify-center">
											{formData.expectedHours}h
										</Badge>
									</div>
								</div>
							</div>
						</div>

						{/* Calculate Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={kpiMutation.isPending}
						>
							{kpiMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							Calculate KPI
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>Result</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Final Score */}
								<div className="text-center p-6 bg-gray-50 rounded-lg">
									<p className="text-sm text-gray-600 mb-2">Final KPI Score</p>
									<p className={cn("text-6xl font-bold", getScoreColor(result.finalScore))}>
										{result.finalScore}
									</p>
									<div className="flex items-center justify-center gap-1 mt-3">
										{Array.from({ length: 5 }).map((_, i) => (
											<Star
												key={i}
												className={cn(
													"w-5 h-5",
													i < performanceLevelStars[result.performanceLevel]
														? "text-amber-400 fill-amber-400"
														: "text-gray-300"
												)}
											/>
										))}
									</div>
									<Badge
										className={cn(
											"mt-2 text-white",
											performanceLevelColors[result.performanceLevel]
										)}
									>
										{result.performanceLevel.toUpperCase()}
									</Badge>
								</div>

								{/* Weighted Scores */}
								<div>
									<h4 className="font-medium text-gray-700 mb-3">Weighted Scores</h4>
									<div className="space-y-3">
										{Object.entries(result.weightedScores)
											.filter(([_, data]) => data !== undefined)
											.map(([key, data]: [string, any]) => (
												<div key={key} className="p-3 bg-gray-50 rounded-lg">
													<div className="flex items-center justify-between text-sm mb-1">
														<span className="capitalize font-medium">{key}</span>
														<span className="text-gray-600">
															Weight: {data.weight}%
														</span>
													</div>
													<div className="flex items-center justify-between">
														<div className="flex-1 mr-4">
															<div className="w-full bg-gray-200 rounded-full h-2">
																<div
																	className="h-2 rounded-full bg-blue-500"
																	style={{ width: `${data.rawScore}%` }}
																/>
															</div>
														</div>
														<span className="font-bold text-lg">
															{Math.round(data.weightedScore * 10) / 10}
														</span>
													</div>
													<p className="text-xs text-gray-500 mt-1">
														Raw: {Math.round(data.rawScore * 10) / 10}
													</p>
												</div>
											))}
									</div>
								</div>

								{/* Thresholds */}
								<div>
									<h4 className="font-medium text-gray-700 mb-3">Performance Thresholds</h4>
									<div className="grid grid-cols-4 gap-2 text-center text-sm">
										<div className="p-2 bg-green-50 rounded">
											<p className="font-bold text-green-600">{result.thresholds.exceptional}+</p>
											<p className="text-xs text-gray-600">Exceptional</p>
										</div>
										<div className="p-2 bg-blue-50 rounded">
											<p className="font-bold text-blue-600">{result.thresholds.high}+</p>
											<p className="text-xs text-gray-600">High</p>
										</div>
										<div className="p-2 bg-amber-50 rounded">
											<p className="font-bold text-amber-600">{result.thresholds.average}+</p>
											<p className="text-xs text-gray-600">Average</p>
										</div>
										<div className="p-2 bg-orange-50 rounded">
											<p className="font-bold text-orange-600">{result.thresholds.below}+</p>
											<p className="text-xs text-gray-600">Below</p>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Adjust the inputs and click Calculate to see results</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
